import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Button } from "../../Components/Button";
import { useTranslation } from "react-i18next";
import FormLabel from "../../Components/FormLabel";
import {
  clearUpdateBusinessService,
  clearUpdateBusinessServiceError,
  clearUpdateServiceAddOn,
  requestCreateBusinessService,
  requestCreateServiceAddOn,
  requestDeleteBusinessService,
  requestGetBusinessService,
  requestListAvailableAddOns,
  requestListBusinessServiceCategories,
  requestListProvinces,
  requestUpdateBusinessService,
  useAuthState,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { NumericFormat } from "react-number-format";
import { classNames, currencyFormat, getErrorMessages } from "../../Utils";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import InputSkeleton from "../../Components/InputSkeleton";
import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { Field, Label, Switch } from "@headlessui/react";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Select from "react-select";
import Divider from "../../Components/Divider";
import { businessServiceOrderStatus } from "../../Constants";
import { Modal } from "../../Components/Modal";
import { ModalAlert } from "../../Components/ModalAlert";

const MAX_REQUIREMENTS = 10;
const MAX_ADDONS = 5;
const minPrice = 1000;
const maxPrice = 50000000;

const ManageBusinessServiceDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const businessServiceDispatch = useBusinessServiceDispatch();
  const {
    request,
    businessService,
    businessServiceLoading,
    businessServiceError,
    businessServiceID,
    businessServiceUpdateLoading,
    businessServiceUpdateError,
    businessServiceCategories,
    loading,
    error,
    businessServiceCreateLoading,
    businessServiceCreateError,
    serviceAddons,
    serviceAddonsLoading,
    serviceAddonsError,
    newServiceAddOn,
    newServiceAddOnError,
    newServiceAddOnLoading,
    businessServiceDeleteLoading,
    businessServiceDeleteError,
    deletedID,
    provinces,
    provincesLoading,
    provincesError,
  } = useBusinessServiceState();

  const { user } = useAuthState();

  const [fieldError, setfieldError] = useState(false);

  const [serviceName, setserviceName] = useState("");
  const [description, setdescription] = useState("");
  const [price, setprice] = useState("");
  const [basePrice, setbasePrice] = useState("");
  const [available, setavailable] = useState(false);
  const [benefits, setbenefits] = useState([]);
  const [addOns, setaddOns] = useState([]);
  const [steps, setsteps] = useState([]);
  const [selectedCategory, setselectedCategory] = useState(null);

  const [serviceNameError, setserviceNameError] = useState(null);
  const [priceError, setpriceError] = useState(null);
  const [basePriceError, setbasePriceError] = useState(null);
  const [descriptionError, setdescriptionError] = useState(null);
  const [categoryError, setcategoryError] = useState(null);
  const [benefitsError, setbenefitsError] = useState(null);
  const [stepErrors, setstepErrors] = useState(
    steps.map(() => ({
      stepNameError: "",
      stepDescriptionError: "",
    }))
  );
  const [submitted, setsubmitted] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const [showAddOnModal, setshowAddOnModal] = useState(false);
  const [showAddOnSuccessModal, setshowAddOnSuccessModal] = useState(false);
  const [showConfirmModal, setshowConfirmModal] = useState(false);
  const [showErrorModal, setshowErrorModal] = useState(false);

  const [addOnName, setaddOnName] = useState("");
  const [addOnDescription, setaddOnDescription] = useState("");
  const [addOnPrice, setaddOnPrice] = useState("");
  const [addOnBasePrice, setaddOnBasePrice] = useState("");

  const [addOnNameError, setaddOnNameError] = useState(null);
  const [addOnPriceError, setaddOnPriceError] = useState(null);
  const [addOnBasePriceError, setaddOnBasePriceError] = useState(null);
  const [addOnSubmitted, setaddOnSubmitted] = useState(false);

  const [selectedProvinces, setselectedProvinces] = useState([]);
  const [selectedCities, setselectedCities] = useState([]);
  const [provinceErrors, setprovinceErrors] = useState([]);
  const [citiesError, setcitiesError] = useState([]);
  const [serviceAreaError, setserviceAreaError] = useState(null);

  const serviceCategories = useMemo(() => {
    return businessServiceCategories.map((item) => ({
      label: item.name,
      value: item.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessServiceCategories]);

  const provinceOptions = useMemo(() => {
    return provinces.map((province) => ({
      label: province.name,
      value: province.id,
      cities: province.cities?.items || [],
    }));
  }, [provinces]);

  useEffect(() => {
    requestListProvinces(businessServiceDispatch, { limit: 50 });

    return () => {
      clearUpdateBusinessService(businessServiceDispatch);
      clearUpdateBusinessServiceError(businessServiceDispatch);
    };
  }, [businessServiceDispatch]);

  useEffect(() => {
    if (submitted) {
      const isserviceNameValid = serviceName.length >= 10;
      const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
      const isBasePriceValid =
        basePrice > 0 ? Number(basePrice) > Number(price) : true;
      const isDescriptionValid = description.length >= 10;
      const isBenefitsValid =
        benefits.filter((item) => item !== null && item.trim() !== "").length >
        0;
      const isCategoryValid = Boolean(selectedCategory);

      if (id) {
        if (selectedProvinces.length === 0) {
          setserviceAreaError("serviceAreaRequired");
        } else {
          const provinceValidationErrors = selectedProvinces.map((province) => {
            return province === null ? "provinceRequired" : "";
          });

          const cityValidationErrors = selectedProvinces.map(
            (province, index) => {
              if (!province) return "";
              const citiesForProvince = selectedCities.filter(
                (city) => city.provinceId === province?.value
              );
              return citiesForProvince.length === 0 ? "cityRequired" : "";
            }
          );

          setserviceAreaError(null);
          setprovinceErrors(provinceValidationErrors);
          setcitiesError(cityValidationErrors);
        }
      }

      if (isserviceNameValid) {
        setErrorMessage("serviceName", "");
      } else {
        if (serviceName.length === 0) {
          setErrorMessage("serviceName", "serviceNameRequired");
        } else {
          setErrorMessage("serviceName", "minimumServiceName");
        }
      }

      if (isPriceValid) {
        setErrorMessage("price", "");
      } else {
        if (price < minPrice) {
          setErrorMessage("price", "minimumPriceIs");
        } else if (price > maxPrice) {
          setErrorMessage("price", "maximumPriceIs");
        } else {
          setErrorMessage(
            "price",
            "Price is required and must be greater than 0."
          );
        }
      }

      if (isBasePriceValid) {
        setErrorMessage("basePrice", "");
      } else {
        setErrorMessage("basePrice", "basePriceRequired");
      }

      if (isDescriptionValid) {
        setErrorMessage("description", "");
      } else {
        if (description.length === 0) {
          setErrorMessage("description", "Description is required.");
        } else {
          setErrorMessage("description", "minimumServiceDescription");
        }
      }

      if (isBenefitsValid) {
        setErrorMessage("benefits", "");
      } else {
        setErrorMessage("benefits", "serviceBenefitMin");
      }

      if (isCategoryValid) {
        setErrorMessage("category", "");
      } else {
        setErrorMessage("category", "categoryRequired");
      }

      steps.forEach((step, index) => {
        const isStepNameValid = step.name && step.name.trim() !== "";
        const isStepDescriptionValid =
          step.description && step.description.trim().length >= 10;

        if (isStepNameValid) {
          setErrorMessage("stepName", "", index);
        } else {
          setErrorMessage("stepName", "stepNameRequired", index);
        }

        if (isStepDescriptionValid) {
          setErrorMessage("stepDescription", "", index);
        } else {
          if (step.description.trim() === "") {
            setErrorMessage(
              "stepDescription",
              "stepDescriptionRequired",
              index
            );
          } else {
            setErrorMessage(
              "stepDescription",
              "stepDescriptionMinimumLength",
              index
            );
          }
        }
      });
    }
  }, [
    serviceName,
    price,
    basePrice,
    description,
    submitted,
    benefits,
    selectedCategory,
    steps,
    selectedProvinces,
    selectedCities,
    id,
  ]);

  useEffect(() => {
    if (addOnSubmitted) {
      const isaddOnNameValid = addOnName.length >= 10;
      const isPriceValid =
        addOnPrice >= 0 && addOnPrice >= minPrice && addOnPrice <= maxPrice;
      const isBasePriceValid =
        addOnBasePrice > 0 ? Number(addOnBasePrice) > Number(addOnPrice) : true;

      if (isaddOnNameValid) {
        setErrorMessage("addOnName", "");
      } else {
        if (addOnName.length === 0) {
          setErrorMessage("addOnName", "addOnNameRequired");
        } else {
          setErrorMessage("addOnName", "minimumAddOnName");
        }
      }

      if (isPriceValid) {
        setErrorMessage("addOnPrice", "");
      } else {
        if (addOnPrice < minPrice) {
          setErrorMessage("addOnPrice", "minimumPriceIs");
        } else if (addOnPrice > maxPrice) {
          setErrorMessage("addOnPrice", "maximumPriceIs");
        } else {
          setErrorMessage(
            "addOnPrice",
            "Price is required and must be greater than 0."
          );
        }
      }

      if (isBasePriceValid) {
        setErrorMessage("addOnBasePrice", "");
      } else {
        setErrorMessage("addOnBasePrice", "basePriceRequired");
      }
    }
  }, [addOnName, addOnPrice, addOnBasePrice, addOnSubmitted]);

  useEffect(() => {
    if (id) {
      requestGetBusinessService(businessServiceDispatch, {
        id,
      });
    } else {
      requestListBusinessServiceCategories(businessServiceDispatch, {
        page: 1,
        limit: 10,
      });
    }
    requestListAvailableAddOns(businessServiceDispatch, {
      limit: 10,
      page: 1,
      owner: user.id,
    });
  }, [id, businessServiceDispatch, user.id]);

  useEffect(() => {
    if (businessService) {
      const {
        name,
        description,
        price,
        basePrice,
        status,
        benefits,
        addOns,
        serviceAreas,
      } = businessService || {};

      if (name) setserviceName(name);
      if (description) setdescription(description);
      if (price) setprice(price);
      if (basePrice) setbasePrice(basePrice);
      if (status === "AVAILABLE") {
        setavailable(true);
      } else {
        setavailable(false);
      }
      if (benefits?.length > 0) {
        setbenefits(benefits);
      }

      if (addOns?.items?.length > 0) {
        const addOn = addOns.items.map((item) => ({
          id: item.addOn.id,
          name: item.addOn.name,
        }));
        setaddOns(addOn);
      }

      if (serviceAreas?.items?.length > 0) {
        const provincesMap = new Map();
        const cities = [];

        serviceAreas.items.forEach((area) => {
          const province = area.city.province;
          const city = area.city;

          if (!provincesMap.has(province.id)) {
            // Find matching province from provinceOptions to include cities data
            const fullProvinceData = provinceOptions.find(
              (p) => p.value === province.id
            );

            provincesMap.set(province.id, {
              label: province.name,
              value: province.id,
              cities: fullProvinceData?.cities || [],
            });
          }

          cities.push({
            label: city.name,
            value: city.id,
            provinceId: province.id,
          });
        });

        setselectedProvinces(Array.from(provincesMap.values()));
        setselectedCities(cities);
      }
    }
  }, [businessService, provinceOptions]);

  useEffect(() => {
    if (businessServiceID) {
      setshowSuccessModal(true);
    }
  }, [businessServiceID]);

  useEffect(() => {
    const parsedErrorCreate = businessServiceCreateError?.response?.body
      ? JSON.parse(businessServiceCreateError.response.body)
      : null;

    const parsedErrorUpdate = businessServiceUpdateError?.response?.body
      ? JSON.parse(businessServiceUpdateError.response.body)
      : null;

    const errorDetail = parsedErrorCreate?.error || parsedErrorUpdate?.error;

    if (errorDetail?.name) {
      setErrorMessage("serviceName", errorDetail.name);
    } else {
      setErrorMessage("serviceName", "");
    }

    if (errorDetail?.categoryID) {
      setErrorMessage("category", errorDetail.categoryID);
    } else {
      setErrorMessage("category", "");
    }

    if (errorDetail?.description) {
      setErrorMessage("description", errorDetail.description);
    } else {
      setErrorMessage("description", "");
    }

    if (errorDetail?.price) {
      setErrorMessage("price", errorDetail.price);
    } else {
      setErrorMessage("price", "");
    }

    if (errorDetail?.basePrice) {
      setErrorMessage("basePrice", errorDetail.basePrice);
    } else {
      setErrorMessage("basePrice", "");
    }
  }, [businessServiceCreateError, businessServiceUpdateError]);

  useEffect(() => {
    if (newServiceAddOn?.id) {
      setshowAddOnModal(false);
      setshowAddOnSuccessModal(true);

      const newAddOn = newServiceAddOn;
      setaddOns([...addOns, { id: newAddOn.id, name: newAddOn.name }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newServiceAddOn?.id]);

  useEffect(() => {
    if (!deletedID) {
      return;
    }
    setshowConfirmModal(false);
    setshowSuccessModal(true);
  }, [deletedID]);

  useEffect(() => {
    if (businessServiceDeleteError) {
      setshowConfirmModal(false);
      setshowErrorModal(true);
    }
  }, [businessServiceDeleteError]);

  const getAvailableProvinceOptions = (currentIndex) => {
    return provinceOptions.filter(
      (opt) =>
        !selectedProvinces.some(
          (p, idx) => idx !== currentIndex && p?.value === opt.value
        )
    );
  };

  const getCityOptions = (province) => {
    if (!province) return [];

    const provinceData = provinceOptions.find(
      (p) => p.value === province.value
    );

    if (!provinceData || !provinceData.cities) return [];

    return provinceData.cities
      .map((city) => ({
        label: city.name,
        value: city.id,
        provinceId: province.value,
      }))
      .filter((cityOption) => {
        return true;
      });
  };

  const validateInput = () => {
    let currentValidCategory = true;
    let currentValidPrice = true;
    let currentValidBasePrice = true;
    let currentValidserviceName = true;
    let currentValidDescription = true;
    let currentValidBenefits = true;
    let currentValidSteps = true;
    let currentValidProvinces = true;
    let currentValidCities = true;

    const isserviceNameValid = serviceName.length >= 10;
    const isPriceValid = price >= 0 && price >= minPrice && price <= maxPrice;
    const isBasePriceValid =
      basePrice > 0 ? Number(basePrice) > Number(price) : true;
    const isDescriptionValid = description.length >= 10;
    const isBenefitsValid =
      benefits.filter((item) => item !== null && item.trim() !== "").length > 0;
    const isCategoryValid = Boolean(selectedCategory);
    const isStepsValid = steps.every(
      (step) => step.name && step.status && step.description.length >= 10
    );

    let isProvincesValid = true;
    let isCitiesValid = true;

    if (id) {
      isProvincesValid =
        selectedProvinces.length > 0 &&
        selectedProvinces.every((province) => province !== null);

      isCitiesValid = selectedProvinces.every((province) => {
        if (!province) return false;
        const citiesForProvince = selectedCities.filter(
          (city) => city.provinceId === province.value
        );
        return citiesForProvince.length > 0;
      });
    }

    currentValidserviceName = isserviceNameValid;
    currentValidPrice = isPriceValid;
    currentValidBasePrice = isBasePriceValid;
    currentValidDescription = isDescriptionValid;
    currentValidBenefits = isBenefitsValid;
    currentValidCategory = id ? true : isCategoryValid;
    currentValidSteps = steps.length === 0 ? true : isStepsValid;
    currentValidProvinces = isProvincesValid;
    currentValidCities = isCitiesValid;

    return (
      currentValidCategory &&
      currentValidserviceName &&
      currentValidPrice &&
      currentValidBasePrice &&
      currentValidDescription &&
      currentValidBenefits &&
      currentValidSteps &&
      currentValidProvinces &&
      currentValidCities
    );
  };

  const setErrorMessage = (field, message, index) => {
    switch (field) {
      case "serviceName":
        setserviceNameError(message);
        break;
      case "description":
        setdescriptionError(message);
        break;
      case "price":
        setpriceError(message);
        break;
      case "basePrice":
        setbasePriceError(message);
        break;
      case "benefits":
        setbenefitsError(message);
        break;
      case "category":
        setcategoryError(message);
        break;
      case "stepName":
        setstepErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[index] = { ...newErrors[index], stepNameError: message };
          return newErrors;
        });
        break;
      case "stepDescription":
        setstepErrors((prevErrors) => {
          const newErrors = [...prevErrors];
          newErrors[index] = {
            ...newErrors[index],
            stepDescriptionError: message,
          };
          return newErrors;
        });
        break;
      case "addOnName":
        setaddOnNameError(message);
        break;
      case "addOnPrice":
        setaddOnPriceError(message);
        break;
      case "addOnBasePrice":
        setaddOnBasePriceError(message);
        break;
      default:
        break;
    }
  };

  const onCreate = (update = false) => {
    setsubmitted(true);
    const valid = validateInput();

    if (valid) {
      setfieldError(false);
      const input = {
        status: available ? "AVAILABLE" : "UNAVAILABLE",
        name: serviceName,
        description,
        price,
        benefits,
        addOns: addOns.map((addOn) => addOn.id),
      };

      if (basePrice) {
        input.basePrice = basePrice;
      }

      if (update) {
        if (selectedCities.length > 0) {
          input.serviceAreas = selectedCities.map((city) => city.value);
        }

        requestUpdateBusinessService(businessServiceDispatch, {
          id: businessService?.id,
          input,
        });
      } else {
        input.steps = steps;
        input.categoryID = selectedCategory.value;
        requestCreateBusinessService(businessServiceDispatch, {
          input,
        });
      }
    } else {
      setfieldError(true);
    }
  };

  const updateBenefits = (index, value) => {
    const updatedBenefits = [...benefits];
    updatedBenefits[index] = value;
    setbenefits(updatedBenefits);
  };

  const handleAddBenefit = () => {
    updateBenefits(benefits.length, "");
  };

  const handleBenefitsChange = (index, value) => {
    updateBenefits(index, value);
  };

  const handleRemoveBenefit = (index) => {
    const updatedBenefits = [...benefits];
    updatedBenefits.splice(index, 1);
    setbenefits(updatedBenefits);
  };

  const handleAddOnChange = (event, index) => {
    const selectedAddOnId = event.target.value;
    const selectedAddOn = serviceAddons.find(
      (addOn) => addOn.id === selectedAddOnId
    );

    if (selectedAddOn) {
      const updatedAddOns = [...addOns];
      updatedAddOns[index] = {
        id: selectedAddOn.id,
        name: selectedAddOn.name,
      };
      setaddOns(updatedAddOns);
    }
  };

  const handleRemoveAddOn = (index) => {
    const updatedAddOns = [...addOns];
    updatedAddOns.splice(index, 1);
    setaddOns(updatedAddOns);
  };

  const handleAddNewAddOn = () => {
    const availableAddOns = serviceAddons.filter(
      (serviceAddOn) => !addOns.some((addOn) => addOn.id === serviceAddOn.id)
    );

    if (availableAddOns.length > 0) {
      const newAddOn = availableAddOns[0];
      setaddOns([...addOns, { id: newAddOn.id, name: newAddOn.name }]);
    }
  };

  const handleStepChange = (event, index) => {
    const { name, value } = event.target;
    const updatedSteps = [...steps];
    updatedSteps[index] = { ...updatedSteps[index], [name]: value };
    setsteps(updatedSteps);
  };

  const handleAddNewStep = () => {
    const availableStep = businessServiceOrderStatus.find(
      (status) => !steps.some((step) => step.status === status.value)
    );
    if (availableStep) {
      setsteps([
        ...steps,
        {
          status: availableStep.value,
          position: steps.length + 1,
          name: "",
          description: "",
          completeDescription: "",
        },
      ]);
    }
  };

  const handleRemoveStep = (index) => {
    const updatedSteps = steps.filter((_, i) => i !== index);
    setsteps(updatedSteps);
  };

  const handleRemoveProvince = (index) => {
    const province = selectedProvinces[index];

    const updatedProvinces = [...selectedProvinces];
    updatedProvinces.splice(index, 1);
    setselectedProvinces(updatedProvinces);

    if (province) {
      setselectedCities((prevCities) =>
        prevCities.filter((city) => city.provinceId !== province.value)
      );
    }

    setprovinceErrors((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

    setcitiesError((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleProvinceChange = (selectedOption, index) => {
    const oldProvince = selectedProvinces[index];

    setselectedProvinces((prev) => {
      const updated = [...prev];
      updated[index] = selectedOption;
      return updated;
    });

    if (oldProvince) {
      setselectedCities((prevCities) =>
        prevCities.filter((city) => city.provinceId !== oldProvince.value)
      );
    }
  };

  const handleCitiesChange = (selectedOptions, province) => {
    setselectedCities((prevCities) => {
      const otherCities = prevCities.filter(
        (city) => city.provinceId !== province?.value
      );

      const newSelections = Array.isArray(selectedOptions)
        ? selectedOptions
        : [];
      const allCities = [...otherCities, ...newSelections];

      const uniqueCitiesMap = new Map(
        allCities.map((city) => [city.value, city])
      );

      return Array.from(uniqueCitiesMap.values());
    });
  };

  const navigateToServiceList = () => {
    setshowSuccessModal(false);
    if (!businessServiceUpdateError || !businessServiceCreateError) {
      navigate(`/manage-business-service/${businessService?.categoryID}`);
      clearUpdateBusinessService(businessServiceDispatch);
    }
  };

  const clearAddOnModalInput = () => {
    setaddOnName("");
    setaddOnDescription("");
    setaddOnPrice("");
    setaddOnBasePrice("");
  };

  const resetErrors = () => {
    setaddOnNameError("");
    setaddOnPriceError("");
    setaddOnBasePriceError("");
  };

  const clearInputErrorMessages = () => {
    setErrorMessage("addOnName", "");
    setErrorMessage("addOnPrice", "");
    setErrorMessage("addOnBasePrice", "");
  };

  const validateAddOnInput = () => {
    let currentValidAddonName = true;
    let currentValidPrice = true;
    let currentValidBasePrice = true;

    const isAddOnNameValid = addOnName.length >= 10;
    const isPriceValid =
      addOnPrice >= 0 && addOnPrice >= minPrice && price <= maxPrice;
    const isBasePriceValid =
      addOnBasePrice > 0 ? Number(addOnBasePrice) > Number(addOnPrice) : true;

    currentValidAddonName = isAddOnNameValid;
    currentValidPrice = isPriceValid;
    currentValidBasePrice = isBasePriceValid;

    return currentValidAddonName && currentValidPrice && currentValidBasePrice;
  };

  const onCreateAddOn = () => {
    setaddOnSubmitted(true);
    const validForm = validateAddOnInput();

    if (validForm) {
      const input = {
        name: addOnName,
        description: addOnDescription,
        price: addOnPrice,
      };

      if (addOnBasePrice) {
        input.basePrice = addOnBasePrice;
      }

      requestCreateServiceAddOn(businessServiceDispatch, {
        input,
      });
    }
  };

  const onDelete = () => {
    requestDeleteBusinessService(businessServiceDispatch, {
      id,
    });
  };

  const renderAddOnModal = () => {
    return (
      <Modal
        show={showAddOnModal}
        onClose={() => {
          setshowAddOnModal(false);
          clearAddOnModalInput();
          resetErrors();
          clearInputErrorMessages();
          setaddOnSubmitted(false);
        }}
        title={t("businessServiceAddonCreateTitle")}
        footer={
          <div className="p-4 flex justify-end items-center bg-gray-50 rounded-b-lg">
            <Button
              type="submit"
              disabled={newServiceAddOnLoading}
              loading={newServiceAddOnLoading}
              title="Save"
              onClick={onCreateAddOn}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4 text-sm text-gray-700">
          {newServiceAddOnError && (
            <Alert
              title="Error"
              type="error"
              message={newServiceAddOnError?.message || "newServiceAddOnError."}
              errors={newServiceAddOnError?.errors}
            />
          )}
          <div>
            <FormLabel htmlFor="addOnName" label={t("Name")} required />
            <input
              type="text"
              name="addOnName"
              id="addOnName"
              value={addOnName}
              placeholder={t("enterAddOnName")}
              onChange={(e) => setaddOnName(e.target.value)}
              className={classNames(
                addOnNameError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
            />
            {addOnNameError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(addOnNameError)}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="addOnDescription" label={t("Description")} />
            <textarea
              name="addOnDescription"
              id="addOnDescription"
              value={addOnDescription}
              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500
              mt-1 block w-full text-sm rounded-md shadow-sm resize-none"
              placeholder={t("enterAddOnDescription")}
              onChange={(e) => setaddOnDescription(e.target.value)}
              rows={4}
              maxLength={480}
              minLength={10}
            />
          </div>
          <div>
            <FormLabel htmlFor="addOnPrice" label={t("Price")} required />
            <NumericFormat
              type="text"
              name="addOnPrice"
              id="addOnPrice"
              className={classNames(
                addOnPriceError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
              placeholder={t("Enter Price")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              value={addOnPrice}
              onValueChange={(value) => {
                setaddOnPrice(value.value);
                if (value.value < minPrice) {
                  setErrorMessage("addOnPrice", "minimumPriceIs");
                } else if (value.value > maxPrice) {
                  setErrorMessage("addOnPrice", "maximumPriceIs");
                } else {
                  setErrorMessage("addOnPrice", "");
                }
              }}
            />
            {addOnPriceError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(addOnPriceError, {
                  minimumPrice: currencyFormat(minPrice),
                  maximumPrice: currencyFormat(maxPrice),
                })}
              </div>
            )}
          </div>
          <div>
            <FormLabel htmlFor="addOnBasePrice" label={t("basePrice")} />
            <NumericFormat
              type="text"
              name="addOnBasePrice"
              id="addOnBasePrice"
              className={classNames(
                addOnBasePriceError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                "mt-1 block w-full text-sm rounded-md shadow-sm"
              )}
              placeholder={t("enterBasePrice")}
              thousandSeparator="."
              decimalSeparator=","
              displayType="input"
              prefix={"Rp"}
              value={addOnBasePrice}
              onValueChange={(value) => {
                setaddOnBasePrice(value.value);
                if (value.value > 0 && value.value < Number(addOnPrice)) {
                  setErrorMessage("addOnBasePrice", "basePriceRequired");
                } else {
                  setErrorMessage("addOnBasePrice", "");
                }
              }}
            />
            {addOnBasePriceError && (
              <div className="mt-1 text-red-600 text-sm">
                {t(addOnBasePriceError)}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  };

  if (!businessService || businessServiceError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={businessServiceError?.message || "businessServiceNotFound"}
          errors={businessServiceError?.errors}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-4">
        <div className="bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm sm:overflow-hidden rounded-md sm:rounded-lg lg:col-start-1 lg:col-span-2">
          {renderAddOnModal()}
          <div className="space-y-6 px-4 py-6 sm:p-6">
            <ModalAlert
              open={showConfirmModal}
              setOpen={(open) => {
                setshowConfirmModal(open);
              }}
              title="deleteBusinessService"
              description="deleteBusinessServiceConfirmation"
              confirmButtonText="Yes"
              cancelButtonText="Cancel"
              loadingButton={businessServiceDeleteLoading}
              disabledButton={businessServiceDeleteLoading}
              onConfirm={onDelete}
              onCancel={() => {
                setshowConfirmModal(false);
                if (request) {
                  request.cancel();
                }
              }}
            />
            {businessServiceDeleteError && (
              <ModalConfirm
                open={showErrorModal}
                type="ALERT"
                setOpen={(open) => {
                  setshowErrorModal(open);
                }}
                title="Error"
                description={getErrorMessages(businessServiceDeleteError)}
              />
            )}
            {fieldError && (
              <Alert
                title="Incomplete Form"
                type="warning"
                message="Please fill out all fields."
              />
            )}
            {(businessServiceUpdateError || businessServiceCreateError) && (
              <Alert
                type="error"
                title="Error"
                message={
                  businessServiceUpdateError?.message ||
                  businessServiceCreateError?.message ||
                  "Error Update Business Service"
                }
                errors={
                  businessServiceUpdateError?.errors ||
                  businessServiceCreateError?.errors
                }
              />
            )}

            {(businessServiceID || deletedID) && (
              <ModalConfirm
                open={showSuccessModal}
                setOpen={(open) => {
                  setshowSuccessModal(open);
                }}
                title="Success"
                description={
                  deletedID
                    ? "businessServiceSuccessDeleted"
                    : id
                    ? "businessServiceSuccessSaved"
                    : "businessServiceSuccessCreated"
                }
                type="SUCCESS"
                onConfirm={navigateToServiceList}
              />
            )}
            {newServiceAddOn?.id && (
              <ModalConfirm
                open={showAddOnSuccessModal}
                setOpen={(open) => {
                  setshowAddOnSuccessModal(open);
                }}
                title="Success"
                description="serviceAddOnSuccessCreated"
                type="SUCCESS"
                onConfirm={() => {
                  setshowAddOnSuccessModal(false);
                  clearUpdateServiceAddOn(businessServiceDispatch);
                  clearAddOnModalInput();
                  setaddOnSubmitted(false);
                }}
              />
            )}
            <div className="grid grid-cols-1 gap-4">
              {businessServiceLoading ||
              businessServiceUpdateLoading ||
              businessServiceCreateLoading ? (
                <div className="flex flex-col gap-4">
                  <InputSkeleton rows={2} />
                  <div className="flex-1 space-y-2 animate-pulse">
                    <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
                    <div className="block h-44 bg-gray-300 rounded-md"></div>
                  </div>
                  <InputSkeleton rows={2} />
                  <div className="flex-1 space-y-2 animate-pulse">
                    <div className="w-36 h-4 bg-gray-300 rounded-md"></div>
                    <div className="block h-44 bg-gray-300 rounded-md"></div>
                  </div>
                </div>
              ) : (
                <div className="col-span-6 sm:col-span-3 space-y-6">
                  <div>
                    <FormLabel
                      htmlFor="serviceName"
                      label={t("serviceName")}
                      required
                    />
                    <input
                      type="text"
                      name="serviceName"
                      id="serviceName"
                      value={serviceName}
                      className={classNames(
                        serviceNameError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("enterServiceName")}
                      onChange={(e) => setserviceName(e.target.value)}
                      maxLength={120}
                      minLength={10}
                    />
                    {serviceNameError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(serviceNameError)}
                      </div>
                    )}
                  </div>
                  {!id && (
                    <div>
                      <FormLabel
                        htmlFor="serviceCategory"
                        label={t("Category")}
                        required
                      />
                      <Select
                        name="serviceCategory"
                        styles={{
                          control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor:
                              categoryError && !state.isFocused ? "red" : "",
                          }),
                        }}
                        className="mt-1 text-sm"
                        classNamePrefix="select2-selection"
                        options={serviceCategories}
                        placeholder={t("Select Category")}
                        value={selectedCategory}
                        onChange={(selectedOption) => {
                          setselectedCategory(selectedOption);
                        }}
                        isDisabled={loading || error}
                        isLoading={loading}
                      />
                      {categoryError && (
                        <div className="mt-1 text-red-600 text-sm">
                          {t(categoryError)}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label
                      htmlFor="mainbusiness-enabled"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("Availability")}
                    </label>
                    <Field
                      as="div"
                      className="flex items-center mt-1 py-2 px-3 border border-gray-300 rounded-md"
                    >
                      <Switch
                        checked={available}
                        onChange={setavailable}
                        className={classNames(
                          available ? "bg-blue-600" : "bg-gray-200",
                          "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        )}
                      >
                        <span className="sr-only">Use setting</span>
                        <span
                          className={classNames(
                            available ? "translate-x-5" : "translate-x-0",
                            "pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                          )}
                        >
                          <span
                            className={classNames(
                              available
                                ? "opacity-0 ease-out duration-100"
                                : "opacity-100 ease-in duration-200",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-gray-400"
                              fill="none"
                              viewBox="0 0 12 12"
                            >
                              <path
                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                stroke="currentColor"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span
                            className={classNames(
                              available
                                ? "opacity-100 ease-in duration-200"
                                : "opacity-0 ease-out duration-100",
                              "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                            )}
                            aria-hidden="true"
                          >
                            <svg
                              className="h-3 w-3 text-blue-600"
                              fill="currentColor"
                              viewBox="0 0 12 12"
                            >
                              <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                            </svg>
                          </span>
                        </span>
                      </Switch>
                      <Label as="span" className="ml-3">
                        <span className="text-sm font-medium text-gray-900">
                          {t("available")}
                        </span>
                      </Label>
                    </Field>
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="description"
                      label={t("Description")}
                      required
                    />
                    <textarea
                      name="description"
                      id="description"
                      value={description}
                      className={classNames(
                        descriptionError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm resize-none"
                      )}
                      placeholder={t("enterServiceDescription")}
                      onChange={(e) => setdescription(e.target.value)}
                      rows={4}
                      maxLength={480}
                      minLength={10}
                    />

                    {descriptionError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(descriptionError)}
                      </div>
                    )}
                  </div>
                  {id && (
                    <>
                      <div>
                        <FormLabel
                          htmlFor="provinces"
                          label={t("serviceArea")}
                          required
                        />
                        {selectedProvinces.map((province, index) => (
                          <div key={index} className="flex gap-2 mb-1">
                            <div className="flex-1">
                              <Select
                                name="provinces"
                                styles={{
                                  control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor:
                                      provinceErrors[index] && !state.isFocused
                                        ? "red"
                                        : "",
                                  }),
                                }}
                                className="text-sm"
                                classNamePrefix="select2-selection"
                                options={getAvailableProvinceOptions(index)}
                                placeholder={t("selectProvince")}
                                value={province}
                                onChange={(selectedOption) =>
                                  handleProvinceChange(selectedOption, index)
                                }
                                isDisabled={provincesLoading || provincesError}
                                isLoading={provincesLoading}
                              />
                              {provinceErrors[index] && (
                                <div className="mt-1 text-red-600 text-sm">
                                  {t(provinceErrors[index])}
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <Select
                                name="cities"
                                styles={{
                                  control: (baseStyles, state) => ({
                                    ...baseStyles,
                                    borderColor:
                                      citiesError[index] && !state.isFocused
                                        ? "red"
                                        : "",
                                  }),
                                }}
                                className="text-sm"
                                classNamePrefix="select2-selection"
                                options={getCityOptions(province)}
                                placeholder={t("selectCity")}
                                value={selectedCities.filter(
                                  (city) => city.provinceId === province?.value
                                )}
                                onChange={(selectedOptions) =>
                                  handleCitiesChange(selectedOptions, province)
                                }
                                isMulti
                                isDisabled={!province || provincesLoading}
                                isLoading={provincesLoading}
                              />
                              {citiesError[index] && (
                                <div className="mt-1 text-red-600 text-sm">
                                  {t(citiesError[index])}
                                </div>
                              )}
                            </div>
                            <div>
                              <button
                                onClick={() => handleRemoveProvince(index)}
                                className="flex items-center py-2 px-4 text-sm rounded-md shadow-sm border border-gray-300"
                              >
                                <TrashIcon className="h-5 w-5 text-red-500 cursor-pointer" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {serviceAreaError && (
                          <div className="mt-1 text-red-600 text-sm">
                            {t(serviceAreaError)}
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setselectedProvinces([...selectedProvinces, null]);
                          }}
                          className="flex px-4 py-2 justify-center items-center mt-2 text-sm rounded-md shadow-sm border border-gray-300"
                          disabled={
                            selectedProvinces.filter((p) => p !== null)
                              .length === provinceOptions.length ||
                            provincesLoading ||
                            provincesError
                          }
                        >
                          <PlusIcon
                            className={`h-5 w-5 ${
                              selectedProvinces.filter((p) => p !== null)
                                .length === provinceOptions.length ||
                              provincesLoading ||
                              provincesError
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-blue-600"
                            }`}
                          />
                        </button>
                      </div>
                    </>
                  )}
                  <div>
                    <FormLabel htmlFor="price" label={t("Price")} required />
                    <NumericFormat
                      type="text"
                      name="price"
                      id="price"
                      className={classNames(
                        priceError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("Enter Price")}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="input"
                      prefix={"Rp"}
                      value={price}
                      onValueChange={(value) => {
                        setprice(value.value);
                        if (value.value < minPrice) {
                          setErrorMessage("price", "minimumPriceIs");
                        } else if (value.value > maxPrice) {
                          setErrorMessage("price", "maximumPriceIs");
                        } else {
                          setErrorMessage("price", "");
                        }
                      }}
                    />
                    {priceError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(priceError, {
                          minimumPrice: currencyFormat(minPrice),
                          maximumPrice: currencyFormat(maxPrice),
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel htmlFor="basePrice" label={t("basePrice")} />
                    <NumericFormat
                      type="text"
                      name="basePrice"
                      id="basePrice"
                      className={classNames(
                        basePriceError
                          ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                        "mt-1 block w-full text-sm rounded-md shadow-sm"
                      )}
                      placeholder={t("enterBasePrice")}
                      thousandSeparator="."
                      decimalSeparator=","
                      displayType="input"
                      prefix={"Rp"}
                      value={basePrice}
                      onValueChange={(value) => {
                        setbasePrice(value.value);
                        if (value.value > 0 && value.value < Number(price)) {
                          setErrorMessage("basePrice", "basePriceRequired");
                        } else {
                          setErrorMessage("basePrice", "");
                        }
                      }}
                    />
                    {basePriceError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(basePriceError)}
                      </div>
                    )}
                  </div>
                  <div>
                    <FormLabel
                      htmlFor="serviceBenefit"
                      label={t("serviceBenefit")}
                      required
                    />
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex flex-col">
                        <div className="flex w-full gap-4">
                          <div className="flex-1">
                            <input
                              type="text"
                              name="serviceBenefit"
                              id="serviceBenefit"
                              value={benefit}
                              className={classNames(
                                benefitsError || benefit.trim() === ""
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                "mt-1 block w-full text-sm rounded-md shadow-sm"
                              )}
                              placeholder={t("enterBenefit")}
                              onChange={(e) =>
                                handleBenefitsChange(
                                  index,
                                  e.target.value,
                                  benefit.description
                                )
                              }
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveBenefit(index)}
                            className="flex items-center mt-1 text-sm rounded-md shadow-sm border border-gray-300 px-4"
                          >
                            <TrashIcon className="h-5 w-5 text-red-500 cursor-pointer" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {benefitsError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {t(benefitsError)}
                      </div>
                    )}
                    <button
                      onClick={handleAddBenefit}
                      className="flex px-4 py-2 justify-center items-center mt-2 text-sm rounded-md shadow-sm border border-gray-300"
                      disabled={benefits.length === MAX_REQUIREMENTS}
                    >
                      <PlusIcon
                        className={`h-5 w-5 ${
                          benefits.length === MAX_REQUIREMENTS
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-blue-600"
                        }`}
                      />
                    </button>
                  </div>
                  <div>
                    <div className="flex justify-between">
                      <FormLabel htmlFor="Add-On" label={t("Add-On")} />
                      <button
                        type="button"
                        onClick={() => {
                          setshowAddOnModal(true);
                        }}
                        className="text-blue-600 text-sm font-medium"
                      >
                        {t("Create New")}
                      </button>
                    </div>
                    {addOns.map((addOn, index) => (
                      <div key={index} className="flex flex-col">
                        <div className="flex w-full gap-4">
                          <div className="flex-1">
                            <select
                              id="Add-On"
                              name="Add-On"
                              onChange={(event) =>
                                handleAddOnChange(event, index)
                              }
                              disabled={
                                serviceAddonsLoading || serviceAddonsError
                              }
                              value={addOn.id}
                              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500 text-sm"
                            >
                              {serviceAddons.map((addonOption, index) => (
                                <option
                                  key={index}
                                  value={addonOption.id}
                                  disabled={addOns.some(
                                    (addon) => addon.id === addonOption.id
                                  )}
                                >
                                  {t(addonOption.name)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={() => handleRemoveAddOn(index)}
                            className="flex items-center mt-1 text-sm rounded-md shadow-sm border border-gray-300 px-4"
                          >
                            <TrashIcon className="h-5 w-5 text-red-500 cursor-pointer" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      onClick={handleAddNewAddOn}
                      className="flex px-4 py-2 justify-center items-center mt-2 text-sm rounded-md shadow-sm border border-gray-300"
                      disabled={
                        addOns.length === MAX_ADDONS ||
                        addOns.length === serviceAddons.length
                      }
                    >
                      <PlusIcon
                        className={`h-5 w-5 ${
                          addOns.length === MAX_ADDONS ||
                          addOns.length === serviceAddons.length
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-blue-600"
                        }`}
                      />
                    </button>
                  </div>
                  {!id && (
                    <div>
                      <FormLabel htmlFor="steps" label={t("steps")} />
                      {steps.map((step, index) => (
                        <Fragment key={index}>
                          <div
                            key={index}
                            className={classNames(
                              index !== 0 && "mt-7",
                              "flex flex-col mb-4 gap-4"
                            )}
                          >
                            <div className="flex w-full justify-between">
                              <div className="flex items-center">
                                <span className="text-sm">
                                  {t("step", { count: index + 1 })}
                                </span>
                              </div>
                              <button
                                onClick={() => handleRemoveStep(index)}
                                className="flex items-center mt-1 text-sm rounded-md shadow-sm border border-gray-300 px-4 py-2"
                              >
                                <TrashIcon className="h-5 w-5 text-red-500 cursor-pointer" />
                              </button>
                            </div>
                            <div className="flex w-full gap-4">
                              <div className="flex-1">
                                <FormLabel
                                  htmlFor="steps"
                                  label={t("stepName")}
                                  required
                                />
                                <input
                                  type="text"
                                  name="name"
                                  placeholder={t("enterStepName")}
                                  value={step.name}
                                  onChange={(event) =>
                                    handleStepChange(event, index)
                                  }
                                  className={classNames(
                                    stepErrors[index]?.stepNameError
                                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                    "mt-1 block w-full text-sm rounded-md shadow-sm"
                                  )}
                                  maxLength={50}
                                />
                                {stepErrors[index]?.stepNameError && (
                                  <div className="mt-1 text-red-600 text-sm">
                                    {t(stepErrors[index].stepNameError)}
                                  </div>
                                )}
                              </div>

                              <div className="flex-1">
                                <FormLabel
                                  htmlFor="status"
                                  label={t("Status")}
                                  required
                                />
                                <select
                                  name="status"
                                  onChange={(event) =>
                                    handleStepChange(event, index)
                                  }
                                  value={step.status}
                                  className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500 text-sm"
                                >
                                  {businessServiceOrderStatus.map(
                                    (status, index) => (
                                      <option
                                        key={index}
                                        value={status.value}
                                        disabled={steps.some(
                                          (s) =>
                                            s.status === status.value &&
                                            s !== step
                                        )}
                                      >
                                        {t(status.label)}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>
                            </div>
                            <div>
                              <FormLabel
                                htmlFor="description"
                                label={t("Description")}
                                required
                              />
                              <textarea
                                name="description"
                                placeholder={t("Enter Description")}
                                value={step.description}
                                onChange={(event) =>
                                  handleStepChange(event, index)
                                }
                                className={classNames(
                                  stepErrors[index]?.stepDescriptionError
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                                  "mt-1 block w-full text-sm rounded-md shadow-sm resize-none"
                                )}
                                rows={4}
                                maxLength={480}
                                minLength={10}
                              />
                              {stepErrors[index]?.stepDescriptionError && (
                                <div className="mt-1 text-red-600 text-sm">
                                  {t(stepErrors[index].stepDescriptionError)}
                                </div>
                              )}
                            </div>

                            <div>
                              <FormLabel
                                htmlFor="completeDescription"
                                label={t("completeDescription")}
                              />
                              <textarea
                                name="completeDescription"
                                placeholder={t("Enter Description")}
                                value={step.completeDescription}
                                onChange={(event) =>
                                  handleStepChange(event, index)
                                }
                                className="mt-2 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-0 focus:border-blue-500 text-sm resize-none"
                                rows={4}
                                maxLength={480}
                                minLength={10}
                              />
                            </div>
                          </div>
                          {steps.length !== index + 1 && steps.length > 1 && (
                            <Divider />
                          )}
                        </Fragment>
                      ))}
                      <button
                        onClick={handleAddNewStep}
                        className="flex px-4 py-2 justify-center items-center mt-2 text-sm rounded-md shadow-sm border border-gray-300"
                        disabled={businessServiceOrderStatus.every((status) =>
                          steps.some((step) => step.status === status.value)
                        )}
                      >
                        <PlusIcon
                          className={`h-5 w-5 ${
                            businessServiceOrderStatus.every((status) =>
                              steps.some((step) => step.status === status.value)
                            )
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-blue-600"
                          }`}
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="bg-gray-50 flex justify-between rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
            {id && (
              <Button
                color="red"
                outline
                className="w-full sm:w-auto"
                loading={
                  businessServiceLoading ||
                  businessServiceUpdateLoading ||
                  businessServiceCreateLoading ||
                  businessServiceDeleteLoading
                }
                disabled={
                  businessServiceLoading ||
                  businessServiceUpdateLoading ||
                  businessServiceCreateLoading ||
                  businessServiceDeleteLoading
                }
                onClick={() => setshowConfirmModal(true)}
                title={t("delete")}
              />
            )}
            <Button
              className="w-full sm:w-auto"
              loading={
                businessServiceLoading ||
                businessServiceUpdateLoading ||
                businessServiceCreateLoading ||
                businessServiceDeleteLoading
              }
              disabled={
                businessServiceLoading ||
                businessServiceUpdateLoading ||
                businessServiceCreateLoading ||
                businessServiceDeleteLoading
              }
              onClick={() => {
                if (id) {
                  onCreate(true);
                } else {
                  onCreate();
                }
              }}
              title={id ? t("Save") : t("Create")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageBusinessServiceDetail;
